import { Link } from 'react-router-dom';
import { describeStatus, localParts } from '@rydres/shared';
import { Badge, fmtDateTime, fmtTime, formatUsd } from '@rydres/ui';
import type { RiderRide } from '../lib/passenger.js';

/** A ride's own page on this site: the price to accept while one waits, else details, cancelling and receipt. */
export const ridePath = (r: RiderRide) => new URL(r.quoteUrl ?? r.statusUrl).pathname;
/** The date a ride falls on where its driver works, as its time reads. */
export const dayOf = (r: RiderRide) => localParts(new Date(r.startsAt), r.driver.timezone).ymd;

function Avatar({ name, url }: { name: string; url: string | null }) {
  return url ? (
    <img src={url} alt="" className="h-11 w-11 shrink-0 rounded-xl object-cover" />
  ) : (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand font-black text-on-brand">{name.slice(0, 1)}</div>
  );
}

/** One ride in a list: when, where, the driver and its status; `onNotMine` when it only named this person's number. */
export function RideRow({ ride: r, timeOnly, onNotMine }: { ride: RiderRide; timeOnly?: boolean; onNotMine: (r: RiderRide) => void }) {
  const look = describeStatus(r);
  const name = r.driver.name ?? 'Your driver';
  return (
    <div className="rounded-2xl border border-line bg-card">
      <Link to={ridePath(r)} className="flex items-start gap-3 p-4">
        <Avatar name={name} url={r.driver.photoUrl} />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold">{timeOnly ? fmtTime(r.startsAt, r.driver.timezone) : fmtDateTime(r.startsAt, r.driver.timezone)}</span>
            <Badge value={look.label} tone={look.tone} />
          </div>
          <div className="line-clamp-2 text-sm text-ink2">{r.pickupAddress} → {r.dropoffAddress}</div>
          <div className="text-xs text-muted">{name}{r.priceCents !== null ? ` · ${formatUsd(r.priceCents)}` : ''}</div>
        </div>
      </Link>
      {!r.verified && (
        <div className="flex items-center justify-between gap-2 border-t border-line px-4 py-2 text-xs text-muted">
          {r.kind === 'logged' ? `Recorded by ${name} with your number` : 'Booked with your number'}
          <button type="button" onClick={() => onNotMine(r)} className="min-h-8 font-bold text-accent-text underline">Not me</button>
        </div>
      )}
    </div>
  );
}
